<template>
  <Panel hide-filters class="min-h-screen">
    <template slot="panel__header">
      <div class="text-center">
        <div class="text-black font-normal text-3xl mb-2">
          Your Team at {{team.name}}
        </div>
        <button
          class="bg-cardstock-green-500 h-8 my-auto text-white rounded px-2 mb-4"
          v-if="team.isCurrentAdmin"
          @click="isAddingTeachers = !isAddingTeachers"
        >
          Add Teachers
        </button>
      </div>
    </template>
    <div>

      <div
        class="m-4 p-4 bg-cardstock-green-20 rounded"
        v-if="isAddingTeachers"
      >
        <div class="text-center font-bold mb-4 text-xl">
          Who's getting added to your team?
        </div>
        <div class="mb-8">
          <label for="first" class="block text-black text-md mb-2">
            First name
          </label>
          <input
            name="first"
            type="text"
            v-model.trim="proposedFirst"
            class="appearance-none border rounded w-full py-2 px-3 text-gray-600 outline-none focus:border-cardstock-green-500 focus:shadow border-gray-500"
            tabindex="0"
          >
        </div>
        <div class="mb-8">
          <label for="last" class="block text-black text-md mb-2">
            Last name
          </label>
          <input
            name="last"
            type="text"
            class="appearance-none border rounded w-full py-2 px-3 text-gray-600 outline-none focus:border-cardstock-green-500 focus:shadow border-gray-500"
            v-model.trim="proposedLast"
            tabindex="0"
          >
        </div>
        <div class="mb-8">
          <label for="email" class="block text-black text-md mb-2">
            Email
          </label>
          <input
            name="email"
            type="text"
            v-model.trim="proposedEmail"
            class="appearance-none border rounded w-full py-2 px-3 text-gray-600 outline-none focus:border-cardstock-green-500 focus:shadow border-gray-500"
            tabindex="0"
            placeholder="(required)"
            v-validate.disable="'required|email'"
          >
          <span>{{ errors.first('email') }}</span>
        </div>
        <div class="mb-8">
          <label for="areaCode" class="block text-black text-md mb-2">
            Area Code (something that will look familiar to students and families)
          </label>
          <input
            name="areaCode"
            type="text"
            v-model.trim="proposedAreaCode"
            class="appearance-none border rounded w-full py-2 px-3 text-gray-600 outline-none  focus:border-cardstock-green-500 focus:shadow border-gray-500"
            tabindex="0"
            placeholder="(required, three digits)"
            v-validate.disable="'required|integer|length:3'"
            data-vv-as="area code"
          >
          <span>{{ errors.first('areaCode') }}</span>
        </div>
        <div class="mb-8">
          <label for="password" class="block text-black text-md mb-2">
            Password
          </label>
          <input
            name="password"
            type="text"
            v-model="proposedPassword"
            class="appearance-none border rounded w-full py-2 px-3 text-gray-600 outline-none focus:border-cardstock-green-500 focus:shadow border-gray-500"
            tabindex="0"
            v-validate.disable="'required|min:8'"
            placeholder="(required, at least eight characters)"
          >
          <span>{{ errors.first('password') }}</span>
        </div>

        <div class="mt-4 p-4 sticky bottom-0 flex items-right justify-around" >
          <button @click="didNevermind" class="text-white py-2 px-4 rounded cursor-pointer bg-gray-600 text-center whitespace-nowrap z-10 h-10">
            Nevermind
          </button>
          <ButtonSubmit @click.native="didAddTeacher" class="flex-shrink-0 ml-4 h-10 z-10">
            Add Teacher
          </ButtonSubmit>
        </div>
      </div>

      <div class="text-black font-normal text-base mx-4 mt-4">
        You
      </div>
      <div class="flex text-black text-base mx-4 border rounded p-4">
        <div class="flex-grow flex text-black">
          <div class="flex-grow flex flex-col justify-center">
            <div>
              <strong>{{currentTeacher.first}} {{currentTeacher.last}}</strong>
            </div>
            <div>
              {{currentTeacher.email}}
            </div>
            <div>
              {{getTPN(currentTeacher)}}
            </div>
          </div>
        </div>
      </div>

      <div class="text-black font-normal text-base mx-4 mt-4">
        Everyone Else
      </div>
      <div v-if="everyoneElse.length == 0" class="bg-gray-200 rounded p-4 mx-4 mb-8">
        No other teachers on your team
      </div>
      <div
        class="flex text-black text-base mx-4 border rounded p-4 mb-4"
        :class="{'bg-cardstock-yellow-400': isTeacherDisabled(teacher)}"
        v-for="teacher in everyoneElse" :key="teacher.id"
      >
        <div class="flex-grow flex text-black">
          <div class="flex-grow flex flex-col justify-center">
            <div>
              <strong>{{teacher.first}} {{teacher.last}}</strong>
            </div>
            <div>
              {{teacher.email}}
            </div>
            <div>
              {{getTPN(teacher)}}
            </div>
            <div v-show="isTeacherDisabled(teacher)">
              Account Disabled
            </div>
          </div>
        </div>
        <div class="flex flex-col justify-center px-4" v-if="team.isCurrentAdmin && !isTeacherDisabled(teacher)">
          <TeacherDisableButtonForm :teacher="teacher" />
        </div>
      </div>

    </div>
  </Panel>
</template>

<script>
import _filter                    from 'lodash-es/filter'
import _get                       from 'lodash-es/get'

import ButtonSubmit               from '@/components/ButtonSubmit.vue'
import Panel                      from '@/components/Panel.vue'
import PanelDropdown              from '@/components/PanelDropdown.vue'

import AvailablePhoneNumbersQuery from '@/queries/AvailablePhoneNumbers.gql'
import TeamQuery                  from '@/queries/Team.gql'
import TeachersQuery              from '@/queries/Teachers.gql'
import CurrentTeacherQuery        from '@/queries/CurrentTeacher.gql'
import TeacherCreateMutation      from '@/mutations/TeacherCreate.gql'
import TeacherDisableButtonForm   from '@/components/TeacherDisableButtonForm.vue'

import { preloadMany }            from '@/preloader'

const preloadQueries = (to) => {
  return [
    {
      queryName: "team",
      query: TeamQuery,
      variables: {
        teamId: to.params.teamId
      },
      fetchPolicy: 'network-only'
    },
    {
      queryName: 'currentTeacher',
      query: CurrentTeacherQuery,
      variables: {
        teamId: to.params.teamId
      },
      fetchPolicy: 'network-only'
    },
    {
      queryName: 'teachers',
      query: TeachersQuery,
      variables: {
        teamId: to.params.teamId
      },
      fetchPolicy: 'network-only'
    }
  ]
}

export default {
  components: {
    ButtonSubmit,
    Panel,
    PanelDropdown,
    TeacherDisableButtonForm
  },

  props: {
    teamId: { required: true },
    currentUser: { required: true }
  },

  data() {
    return {
      hasAvailablePhoneNumbers: null,
      isAddingTeachers: false,
      proposedFirst: '',
      proposedLast: '',
      proposedEmail: '',
      proposedAreaCode: '',
      proposedPassword: '',
      teachers: []
    }
  },

  beforeRouteEnter(to, from, next) {
    preloadMany({
      queries: preloadQueries(to),
      callback: next
    })
  },

  beforeRouteUpdate(to, from, next) {
    preloadMany({
      queries: preloadQueries(to),
      callback: next,
      mountedVm: this
    })
  },

  computed: {
    everyoneElse() {
      var self = this

      return _filter(this.teachers, (teacher) => {
        return teacher.id != _get(self, 'currentTeacher.id')
      })
    },

    teacherCreateVariables() {
      return {
        teamId: this.teamId,
        first: this.proposedFirst,
        last: this.proposedLast,
        email: this.proposedEmail,
        areaCode: this.proposedAreaCode,
        password: this.proposedPassword
      }
    }
  },

  methods: {

    didAddTeacher() {
      this.$validator.validate()
      .then( (valid) => {
        if (valid == true) {
          this.$apollo.mutate(
            {
              mutation: TeacherCreateMutation,
              variables: this.teacherCreateVariables,
            refetchQueries: [{
            query: TeachersQuery,
            variables: {
              teamId: this.teamId
            },
            fetchPolicy: 'cache-first'
          }]})
          .then((data) => {
            console.log('Cardstock: Successful submission ' + JSON.stringify(data))
            this.didNevermind()
          }).catch((error) => {
            console.log('Cardstock: Error on submission ' + error)
          })
        }
      })
    },

    didNevermind() {
      this.isAddingTeachers = false
      this.proposedFirst =  ''
      this.proposedLast =  ''
      this.proposedEmail =  ''
      this.proposedAreaCode =  ''
      this.proposedPassword =  ''
      this.errors.clear
    },

    getTPN(teacher) {
      return _get(teacher, 'teacherPhoneNumbers[0].formattedNumber')
    },

    isTeacherDisabled(teacher) {
      return _get(teacher, 'user.id') == null
    }
  },

  watch: {
    proposedAreaCode() {
      if (this.proposedAreaCode.length == 3) {
        this.$apollo.query({
          query: AvailablePhoneNumbersQuery,
          variables: {
            teamId: this.teamId,
            areaCode: this.proposedAreaCode
          },
          fetchPolicy: 'no-cache'
        })
        .then( ({data}) => {
          console.info(data)
          if (data.availablePhoneNumbers.length < 6) {
            this.errors.add({
              field: 'areaCode',
              msg: 'This area code does not have numbers available for purchase. Try another!',
              scope: 'remote'
            })
          } else {
            this.errors.remove('areaCode', 'remote')
          }
        })
        .catch( (error) => {
          this.errors.add({
            field: 'areaCode',
            msg: 'This area code does not have numbers available for purchase. Try another!',
            scope: 'remote'
          })
        })
      } else {
        this.errors.remove('areaCode', 'remote')
      }
    }
  },


  apollo: {
    team: {
      query: TeamQuery,
      variables() {
        return {
          teamId: this.teamId
        }
      },
      fetchPolicy: 'cache-first'
    },

    currentTeacher: {
      query: CurrentTeacherQuery,
      variables() {
        return { teamId: this.teamId }
      },
      fetchPolicy: 'cache-first'
    },

    teachers: {
      query: TeachersQuery,
      variables() {
        return { teamId: this.teamId }
      },
      fetchPolicy: 'cache-first'
    }
  }

}
</script>
